import { Cable } from 'lucide-react';

import { CopyButton } from '@/_shared/components/CopyButton';
import { Input } from '@/_shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/_shared/components/ui/tabs';

import { McpAvailableTools } from './McpAvailableTools';

export function McpServerCard(): React.ReactElement {
  const mcpUrl = `${window.location.origin}/mcp`;
  const claudeMcpCommand = `claude mcp add --transport http rudder-lookout ${mcpUrl}`;
  const bearerHeaderExample = 'Authorization: Bearer rai_your_token_here';

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center gap-3 px-4 py-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
          <Cable className="size-5 text-muted-foreground" aria-hidden />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">MCP server</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            OAuth clients handle sign-in automatically. Use access tokens for scripts and CI.
          </p>
        </div>
      </div>

      <div className="border-t border-border px-4 py-3">
        <label htmlFor="mcp-server-url" className="mb-1.5 block text-sm font-medium text-foreground">
          Server URL
        </label>
        <div className="flex items-center gap-2">
          <Input id="mcp-server-url" size="lg" className="flex-1 font-mono" readOnly value={mcpUrl} />
          <CopyButton size="lg" text={mcpUrl} copyLabel="Server URL" copiedFeedback />
        </div>
      </div>

      <div className="border-t border-border px-4 py-3">
        <Tabs defaultValue="claude-ai">
          <div className="flex items-center justify-between gap-3">
            <p className="shrink-0 text-sm font-medium text-foreground">Setup</p>
            <TabsList className="shrink-0">
              <TabsTrigger value="claude-ai">Claude.ai</TabsTrigger>
              <TabsTrigger value="chatgpt">ChatGPT</TabsTrigger>
              <TabsTrigger value="claude-code">Claude Code</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="claude-ai" className="mt-3">
            <p className="mb-2 text-xs text-muted-foreground">Requires Pro, Max, Team, or Enterprise plan</p>
            <ol className="space-y-1.5 text-xs text-muted-foreground">
              <li className="flex gap-2">
                <span className="shrink-0 font-medium text-foreground">1.</span>
                Go to{' '}
                <a
                  href="https://claude.ai/settings/connectors"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-foreground underline decoration-border underline-offset-2 hover:decoration-primary"
                >
                  Settings &rarr; Connectors
                </a>{' '}
                on claude.ai
              </li>
              <li className="flex gap-2">
                <span className="shrink-0 font-medium text-foreground">2.</span>
                Click <strong className="text-foreground">Add custom connector</strong>
              </li>
              <li className="flex gap-2">
                <span className="shrink-0 font-medium text-foreground">3.</span>
                Enter a name and paste the MCP Server URL above
              </li>
              <li className="flex gap-2">
                <span className="shrink-0 font-medium text-foreground">4.</span>
                Approve the connection — sign in to Rudder Lookout if needed
              </li>
              <li className="flex gap-2">
                <span className="shrink-0 font-medium text-foreground">5.</span>
                In a new chat, enable the connector via the <strong className="text-foreground">+</strong> button in the
                composer
              </li>
            </ol>
          </TabsContent>

          <TabsContent value="chatgpt" className="mt-3">
            <p className="mb-2 text-xs text-muted-foreground">Requires Pro or Plus plan with Developer Mode</p>
            <ol className="space-y-1.5 text-xs text-muted-foreground">
              <li className="flex gap-2">
                <span className="shrink-0 font-medium text-foreground">1.</span>
                Enable <strong className="text-foreground">Developer Mode</strong> in Settings &rarr; Advanced Settings
              </li>
              <li className="flex gap-2">
                <span className="shrink-0 font-medium text-foreground">2.</span>
                Go to the <strong className="text-foreground">Apps</strong> tab and click{' '}
                <strong className="text-foreground">Create</strong>
              </li>
              <li className="flex gap-2">
                <span className="shrink-0 font-medium text-foreground">3.</span>
                Enter a name and paste the MCP Server URL above
              </li>
              <li className="flex gap-2">
                <span className="shrink-0 font-medium text-foreground">4.</span>
                Approve the connection — sign in to Rudder Lookout if needed
              </li>
            </ol>
          </TabsContent>

          <TabsContent value="claude-code" className="mt-3">
            <p className="mb-2 text-xs text-muted-foreground">Run this command in your terminal</p>
            <div className="flex items-center gap-2">
              <Input
                size="lg"
                className="flex-1 font-mono"
                readOnly
                value={claudeMcpCommand}
                aria-label="Claude Code MCP setup command"
              />
              <CopyButton size="lg" text={claudeMcpCommand} copyLabel="Setup command" copiedFeedback />
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-4">
          <p className="text-xs font-medium text-foreground">Using an access token?</p>
          <p className="mt-0.5 mb-1.5 text-xs text-muted-foreground">
            For scripts and CI pipelines, pass your token as a Bearer header:
          </p>
          <Input
            size="lg"
            className="font-mono"
            readOnly
            value={bearerHeaderExample}
            aria-label="Bearer authorization header example"
          />
        </div>
      </div>

      <McpAvailableTools />
    </div>
  );
}
